<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="640"
    persistent
    eager
    content-class="radius-picker-dialog"
  >
    <v-card class="radius-picker-card" dark>
      <v-card-title class="radius-picker-title">
        <v-icon left>mdi-map-marker-radius</v-icon>
        Seleccionar radio
      </v-card-title>
      <v-card-text class="radius-picker-content">
        <div ref="mapEl" class="radius-picker-map" style="width: 100%; height: 320px;"></div>
        <div class="radius-picker-controls">
          <div class="radius-value-display">
            <span class="radius-value-label">Radio:</span>
            <span class="radius-value-number">{{ radiusSlider }}</span>
            <span class="radius-value-unit">m</span>
          </div>
          <v-slider
            v-model="radiusSlider"
            :min="100"
            :max="5000"
            :step="100"
            label="Radio (metros)"
            thumb-label
            class="radius-picker-slider"
            @input="updateCircleFromSlider"
          ></v-slider>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn color="primary" @click="confirm">Aceptar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'RadiusPickerDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    getCenter: {
      type: Function,
      default: null,
    },
    getZoom: {
      type: Function,
      default: null,
    },
    getMapMode: {
      type: Function,
      default: null,
    },
    initialRadius: {
      type: Number,
      default: 500,
    },
  },
  data() {
    return {
      radiusSlider: 500,
      mapInstance: null,
      circleSource: null,
      circleFeature: null,
      circleUpdater: null,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.radiusSlider = this.initialRadius;
        this.$nextTick(() => {
          setTimeout(() => this.initMap(), 200);
        });
      } else {
        this.destroyMap();
      }
    },
    initialRadius(val) {
      if (this.value) {
        this.radiusSlider = val;
      }
    },
    radiusSlider() {
      if (this.value && this.circleUpdater) {
        this.circleUpdater();
      }
    },
  },
  methods: {
    initMap() {
      const ol = window.ol;
      const el = this.$refs.mapEl;
      if (!ol || !el || !this.getCenter || !this.getZoom) return;
      if (this.mapInstance) return;

      const center = this.getCenter();
      const zoom = this.getZoom();
      const center3857 = ol.proj.fromLonLat(center);

      this.circleSource = new ol.source.Vector();
      const radiusM = this.radiusSlider || 500;
      const radiusInUnits = this.metersToRadius3857(center[1], radiusM);
      this.circleFeature = new ol.Feature({
        geometry: new ol.geom.Circle(center3857, radiusInUnits),
      });
      this.circleSource.addFeature(this.circleFeature);

      const circleLayer = new ol.layer.Vector({
        source: this.circleSource,
        style: new ol.style.Style({
          fill: new ol.style.Fill({ color: 'rgba(244, 67, 54, 0.35)' }),
          stroke: new ol.style.Stroke({ color: '#E53935', width: 2 }),
        }),
      });

      const mapMode = typeof this.getMapMode === 'function' ? this.getMapMode() : 'day';
      const isNight = mapMode === 'night';
      const tileUrl = isNight
        ? '//api.maptiler.com/maps/f623a750-bc17-4649-8424-b78e06bee07e/256/{z}/{x}/{y}.png?key=G3BcWuok730ChWrFiN06'
        : '//mt0.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}';

      const baseLayer = new ol.layer.Tile({
        source: new ol.source.XYZ({ url: tileUrl }),
      });
      baseLayer.setZIndex(-1);

      this.mapInstance = new ol.Map({
        target: el,
        layers: [
          baseLayer,
          circleLayer,
        ],
        view: new ol.View({
          center: center3857,
          zoom,
        }),
      });

      const updateCircle = () => {
        if (!this.mapInstance || !this.circleFeature) return;
        const c = this.mapInstance.getView().getCenter();
        const lonLat = ol.proj.toLonLat(c);
        const r = this.metersToRadius3857(lonLat[1], this.radiusSlider);
        this.circleFeature.setGeometry(new ol.geom.Circle(c, r));
      };

      this.mapInstance.getView().on('change:center', updateCircle);
      this.circleUpdater = updateCircle;

      setTimeout(() => {
        if (this.mapInstance) this.mapInstance.updateSize();
      }, 200);
    },
    updateCircleFromSlider() {
      if (this.circleUpdater) this.circleUpdater();
    },
    metersToRadius3857(latDeg, meters) {
      const ol = window.ol;
      if (!ol) return meters;
      // En EPSG:3857 1 unidad ≈ 1 m en ecuador; a mayor latitud 1 unidad = 1/cos(lat) m, luego radio_en_unidades = metros * cos(lat)
      const lat = (latDeg * Math.PI) / 180;
      return meters * Math.cos(lat);
    },
    destroyMap() {
      this.circleUpdater = null;
      if (this.mapInstance) {
        this.mapInstance.setTarget(null);
        this.mapInstance = null;
      }
      this.circleSource = null;
      this.circleFeature = null;
    },
    confirm() {
      const ol = window.ol;
      if (!this.mapInstance || !ol) {
        this.$emit('input', false);
        return;
      }
      const center = ol.proj.toLonLat(this.mapInstance.getView().getCenter());
      this.$emit('confirm', {
        center,
        radiusMeters: this.radiusSlider,
      });
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.radius-picker-map {
  width: 100%;
  height: 320px;
  background: #1e1e1e;
}

.radius-picker-card {
  background-color: var(--map-card-background) !important;
}

.radius-picker-title {
  background-color: var(--card-title-background) !important;
  color: #fff !important;
}

.radius-picker-content {
  padding: 16px !important;
}

.radius-picker-controls {
  margin-top: 16px;
}

.radius-value-display {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
}

.radius-value-label {
  font-size: 14px;
}

.radius-value-number {
  font-size: 20px;
  font-weight: 600;
}

.radius-value-unit {
  font-size: 14px;
  opacity: 0.9;
}

.radius-picker-slider {
  margin-top: 8px;
}
</style>
